import { api, unwrap } from '$lib/api/client';
import type { Notification } from '$lib/api/types';
import { session } from '$lib/auth/session.svelte';
import { weekdayIndex } from '$lib/domain/dates';
import { i18n } from '$lib/i18n/i18n.svelte';
import { network } from './network.svelte';
import { recipes } from './recipes.svelte';
import { TOAST_HIDE_MS, toast } from './toast.svelte';

export type NotificationDeps = {
	fetch: () => Promise<Notification[]>;
	/** What the toast says; null for a message with nothing to say here (e.g. a kind shown elsewhere). */
	textOf: (notification: Notification) => Promise<string | null>;
	/** Marks it as shown on the server; true only for the one tab that did. */
	claim: (id: string) => Promise<boolean>;
	show: (text: string) => void;
	canShow: () => boolean;
	/** How long a toast stays before the next message may follow. */
	showFor: number;
};

/**
 * In-app messages, shown once each as a toast, one at a time, so the toast stack (at most three) never pushes one
 * out unseen. A message is claimed on the server right before it is shown, while the tab is visible and online; a
 * claim another tab already won is dropped, so two tabs never show the same message.
 */
export class NotificationQueue {
	private queue: Notification[] = [];
	/** Texts of messages this tab claimed but could not show yet (it was hidden meanwhile); the server won't send them again. */
	private claimed: string[] = [];
	private known = new Set<string>();
	private showing = false;
	/** One pump at a time: refreshes overlap (start, resync), and two pumps would both take the same next message. */
	private pumping = false;

	constructor(private readonly deps: NotificationDeps) {}

	async refresh() {
		let unseen: Notification[];
		try {
			unseen = await this.deps.fetch();
		} catch {
			return; // Messages are a nicety; they come with the next refresh.
		}
		for (const notification of unseen) {
			if (this.known.has(notification.id)) {
				// Still waiting here: take the server's payload, its recipe may have moved to a copy since.
				this.queue = this.queue.map((queued) =>
					queued.id === notification.id ? notification : queued
				);
				continue;
			}
			this.known.add(notification.id);
			this.queue.push(notification);
		}
		await this.pump();
	}

	/** Marks a message the page showed itself (e.g. inline on the day it is about) as seen. */
	async claim(id: string) {
		this.known.add(id);
		this.queue = this.queue.filter((notification) => notification.id !== id);
		try {
			await this.deps.claim(id);
		} catch {
			// It stays unseen and is shown again next time; harmless.
		}
	}

	/** Shows the next message unless one is showing or the tab cannot show it now. */
	async pump(): Promise<void> {
		if (this.pumping) return;
		this.pumping = true;
		try {
			while (!this.showing && this.deps.canShow()) {
				const text = this.claimed.shift();
				if (text !== undefined) this.display(text);
				else if (!this.queue.length || !(await this.claimNext())) return;
			}
		} finally {
			this.pumping = false;
		}
	}

	/**
	 * Claims the next message for this tab and keeps its text until the tab can show it. Resolves false when it has to
	 * wait for the next refresh: the request failed, or the tab was hidden while the text loaded (it stays unclaimed).
	 */
	private async claimNext(): Promise<boolean> {
		const next = this.queue[0];
		try {
			const text = await this.deps.textOf(next);
			// A refresh took the server's newer payload (or the page claimed it) meanwhile; the next round handles that.
			if (this.queue.find((notification) => notification.id === next.id) !== next) return true;
			if (text !== null) {
				if (!this.deps.canShow()) return false;
				if (await this.deps.claim(next.id)) this.claimed.push(text);
			}
		} catch {
			return false;
		}
		this.queue = this.queue.filter((notification) => notification.id !== next.id);
		return true;
	}

	private display(text: string) {
		this.deps.show(text);
		this.showing = true;
		setTimeout(() => {
			this.showing = false;
			void this.pump();
		}, this.deps.showFor);
	}
}

// Swaps made at once ("Mia byttede med fredag") are shown on the plan's day cards instead.
async function textOf(notification: Notification): Promise<string | null> {
	const actor = session.members.find((m) => m.userId === notification.actorUserId)?.name;
	if (!actor) return null;
	if (notification.kind === 'swapApproved') {
		const { firstDate, secondDate } = notification;
		if (!firstDate || !secondDate) return null;
		const days = i18n.t.dates.weekdaysInText;
		return i18n.t.notifications.swapApproved(
			actor,
			days[weekdayIndex(firstDate)],
			days[weekdayIndex(secondDate)]
		);
	}
	if (notification.kind === 'wishPlaced') {
		const recipe = await recipeNamed(notification.recipeId);
		if (!recipe || !notification.firstDate) return null;
		return i18n.t.notifications.wishPlaced(
			recipe,
			i18n.t.dates.weekdaysInText[weekdayIndex(notification.firstDate)]
		);
	}
	if (notification.kind !== 'lookingForward') return null;
	const recipe = await recipeNamed(notification.recipeId);
	return recipe ? i18n.t.notifications.lookingForward(actor, recipe) : null;
}

/** The recipe's name, loading the collection when the recipe is new to this tab. */
async function recipeNamed(recipeId: string | null | undefined): Promise<string | null> {
	if (!recipeId) return null;
	if (!recipes.get(recipeId)) await recipes.refresh();
	return recipes.get(recipeId)?.name ?? null;
}

export const notifications = new NotificationQueue({
	fetch: () => unwrap(api.GET('/api/notifications')),
	textOf,
	claim: async (id) =>
		(
			await unwrap(
				api.POST('/api/notifications/{notificationId}/seen', {
					params: { path: { notificationId: id } }
				})
			)
		).claimed,
	show: (text) => toast.show(text, 'success'),
	canShow: () => network.online && document.visibilityState === 'visible',
	showFor: TOAST_HIDE_MS
});
